import { Bell, ChevronDown, Menu, Search } from "lucide-react";
import { useLocation } from "react-router-dom";

import { useAuth } from "@/shared/contexts/AuthProvider";

import { AccountMenu } from "./AccountMenu";
import { getRouteMeta } from "./nav-config";

/** Two-letter initials from a display name (e.g. "Lamar Falconer" -> "LF"). */
function initialsFrom(name: string | null | undefined): string {
  if (!name) return "BA";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "BA";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export interface TopbarProps {
  /** Opens the mobile sidebar drawer (hamburger). */
  onOpenMobileNav: () => void;
}

export function Topbar({ onOpenMobileNav }: TopbarProps) {
  const { displayName } = useAuth();
  const { pathname } = useLocation();
  const meta = getRouteMeta(pathname);

  return (
    <header className="portal-topbar" data-portal-chrome="topbar">
      <button
        type="button"
        className="portal-iconbtn"
        data-portal-mobile-toggle
        aria-label="Open menu"
        onClick={onOpenMobileNav}
      >
        <Menu aria-hidden />
      </button>

      <h1>{meta.title}</h1>
      {meta.crumb ? (
        <span className="portal-crumb hidden sm:inline">/ {meta.crumb}</span>
      ) : null}

      <div className="flex-1" />

      {/* Visual-only placeholders — no handlers wired yet (flagged in plan). */}
      <button type="button" className="portal-iconbtn" aria-label="Search">
        <Search aria-hidden />
      </button>
      <button
        type="button"
        className="portal-iconbtn"
        aria-label="Notifications"
      >
        <Bell aria-hidden />
        <span className="portal-dot" />
      </button>

      <AccountMenu
        placement="bottom-end"
        trigger={({ open, toggle, ref }) => (
          <button
            ref={ref}
            type="button"
            className="portal-umenu"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={toggle}
          >
            <span className="portal-avatar">{initialsFrom(displayName)}</span>
            <b className="hidden max-w-[160px] truncate sm:inline">
              {displayName ?? "Account"}
            </b>
            <ChevronDown
              aria-hidden
              style={{ transform: open ? "rotate(180deg)" : undefined }}
            />
          </button>
        )}
      />
    </header>
  );
}
